/**
 * Вкладки геометрий с параметрами для каждой геометрии.
 */

import { useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CardSectionGroup } from "./CardSectionGroup";
import type {
  CardTemplateField,
  CardTemplateWithDetails,
  CardValuePayload,
  ObjectCardGeometryItem,
} from "@/types/card-template";

interface GeometryTabsProps {
  geometries: ObjectCardGeometryItem[];
  template: CardTemplateWithDetails | null;
  errors: Record<string, Record<string, string>>;
  onChange: (
    geometryId: number,
    fieldKey: string,
    payload: Partial<CardValuePayload>
  ) => void;
  readonly?: boolean;
  activeGeometryId?: number | null;
  objectId?: number | null;
}

export function GeometryTabs({
  geometries,
  template,
  errors,
  onChange,
  readonly = false,
  activeGeometryId = null,
  objectId = null,
}: GeometryTabsProps) {
  if (geometries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic py-4">
        Геометрии не добавлены
      </p>
    );
  }

  const geoFields =
    template?.fields.filter(
      (f) => f.is_active && f.applies_to === "geometry"
    ) ?? [];

  const geoSections =
    template?.sections.filter(
      (s) => s.applies_to === "geometry" || s.applies_to == null
    ) ?? [];

  const defaultTab = useMemo(() => String(geometries[0].id), [geometries]);
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (!activeGeometryId) return;
    const exists = geometries.some((g) => g.id === activeGeometryId);
    if (exists) {
      setActiveTab(String(activeGeometryId));
    }
  }, [activeGeometryId, geometries]);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <p className="text-xs text-muted-foreground mb-2">
        Здесь редактируются параметры шаблона (в т.ч. geometry_kls_params). Показ и правка линии/полигона на карте — на вкладке «Геометрия» панели объекта.
      </p>
      <TabsList className="w-full flex-wrap h-auto gap-1">
        {geometries.map((g) => (
          <TabsTrigger
            key={g.id}
            value={String(g.id)}
            className={`text-xs ${activeTab === String(g.id) ? "ring-1 ring-blue-400" : ""}`}
          >
            {g.name}
            {g.geometry_kls_type && (
              <span className="ml-1 text-muted-foreground">
                ({g.geometry_kls_type})
              </span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>

      {geometries.map((geo) => {
        const geoKlsId = geo.geometry_kls_id ?? null;
        const filteredFields = geoFields.filter(
          (f) =>
            f.geometry_type_kls_id == null ||
            f.geometry_type_kls_id === geoKlsId,
        );

        const sectionMap = groupFieldsBySectionId(filteredFields);
        const geoErrors = errors[String(geo.id)] ?? {};

        return (
          <TabsContent
            key={geo.id}
            value={String(geo.id)}
            className="mt-3 space-y-4"
          >
            {geoSections
              .sort((a, b) => (a.display_order ?? 100) - (b.display_order ?? 100))
              .map((section) => {
                const sectionFields = sectionMap.get(section.id) ?? [];
                if (sectionFields.length === 0) return null;
                return (
                  <CardSectionGroup
                    key={section.id}
                    section={section}
                    fields={sectionFields}
                    values={geo.values}
                    errors={geoErrors}
                    onChange={(fk, p) => onChange(geo.id, fk, p)}
                    readonly={readonly}
                    objectId={objectId}
                  />
                );
              })}

            {(sectionMap.get(null) ?? []).length > 0 && (
              <CardSectionGroup
                section={null}
                fields={sectionMap.get(null)!}
                values={geo.values}
                errors={geoErrors}
                onChange={(fk, p) => onChange(geo.id, fk, p)}
                readonly={readonly}
                objectId={objectId}
              />
            )}
          </TabsContent>
        );
      })}
    </Tabs>
  );
}

function groupFieldsBySectionId(
  fields: CardTemplateField[]
): Map<number | null, CardTemplateField[]> {
  const map = new Map<number | null, CardTemplateField[]>();
  for (const f of fields) {
    const key = f.section_id ?? null;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(f);
  }
  return map;
}
